import { ChangeEvent, Dispatch, useEffect, useState } from 'react';
import Switch from '@mui/material/Switch';
import {
  Stack,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Typography, styled
} from '@mui/material';
import DatePickerWrapper from '../../components/DatePickerWrapper';
import {useQuery} from 'react-query';
import { InformationPanelFormState } from './InformationPanel';
import orgAPI from '../../apis/orgAPI';
import {useResponsive} from "../../hooks/style";
import useSession from "../../hooks/user/useSession";

interface HerdInfoFormProps {
  formState: InformationPanelFormState;
  setFormState: Dispatch<InformationPanelFormState>;
  usePdfLayout?: boolean;
}

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#01674F',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}));

export default function HerdInformationForm({ formState, setFormState, usePdfLayout = false }: HerdInfoFormProps) {
  const speciesOptions = ['Cow', 'Goat']; // DO NOT CHANGE. THIS IS THE ONLY HARD REFERENCE TO INT

  const foundOrgs = useQuery('allOrgs_herdInfo', () => orgAPI.getAll(), {staleTime: Infinity});

  const [FSPOptions, setFSPOptions] = useState<Record<string, string>[]>([]);
  const [DRPCOptions, setDRPCOptions] = useState<Record<string, string>[]>([]);

  const {isDesktop, isPhone} = useResponsive();
  const {userInfo} = useSession();

  const maskCode = (codeNumb) => {
    const code = codeNumb.toString();
    if (code.length === 8) {
      return code.substring(0, 2) + '-' + code.substring(2, 4) + '-' + code.substring(4, 8)
    }
    return code;
  };

  const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { name, value } } = event;
    const currentValue = formState[name];
    setFormState({ ...formState, [name]: !currentValue });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    let {target: {name, value}} = event;
    const parseList = ['code', 'num_cows'];

    if (name === 'num_cows' && parseInt(value) < 0)
        return;

    setFormState({ ...formState, [name]: parseList.includes(name) && value !== '' ? parseInt(value) : value });
  };

  const handleNumCowsKeydown = (event) => {
    if (['e', '+', '-', '.'].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { target: { name, value } } = event;
    setFormState({ ...formState, [name]: value });
  }

  const handleDateChange = (value) => {
    setFormState({ ...formState, person_providing_info_date: value });
  }

  useEffect(() => {
    const allOrgs = foundOrgs.data;
    // @ts-ignore
    setFSPOptions(allOrgs?.filter((orgObj) => orgObj.org_type_abbrev === 'FSP'));
    // @ts-ignore
    setDRPCOptions(allOrgs?.filter((orgObj) => orgObj.org_type_abbrev === 'DRPC'));
  }, [foundOrgs.data]);

  return (
    <Stack direction="column" spacing={2} sx={{ width: '100%', maxWidth: 'sm' }}>
      <Stack direction="row" sx={{justifyContent: 'space-between'}}>
        <TextField
            required
            id="herd-code-input"
            name="code"
            label="Code"
            value={maskCode(formState.code)}
            sx={{ width: 150, pr: 2 }}
            disabled={true}
        />
        <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
          <Typography>Inactive</Typography>
          <AntSwitch
              checked={formState.is_active}
              onChange={handleSwitchChange}
              inputProps={{ 'aria-label': 'ant design' }}
              name="is_active"
              disabled={userInfo.orgTypeAbbrev === 'RE'}
          />
          <Typography>Active</Typography>
        </Stack>
      </Stack>

      <TextField
        id="herd-owner-input"
        required
        name="owner_name"
        label="Owner Name"
        value={formState.owner_name}
        placeholder='John Jacob Jingleheimer Schmidt'
        onChange={handleInputChange}
        fullWidth
        disabled={userInfo.orgTypeAbbrev === 'RE'}
      />

      <TextField
        id="herd-farm-name-input"
        required
        name="farm_name"
        label="Farm Name"
        value={formState.farm_name}
        onChange={handleInputChange}
        fullWidth
        disabled={userInfo.orgTypeAbbrev === 'RE'}
      />

      <Stack direction="row" spacing={2}>
        <FormControl sx={{ width: 150 }} disabled={userInfo.orgTypeAbbrev === 'RE'}>
          <InputLabel id="herd-species-select-label">Species</InputLabel>
          <Select
            labelId="herd-species-select-label"
            id="herd-species-select"
            name="species_code"
            defaultValue='Bovine'
            value={`${formState.species_code === null ? '' : formState.species_code}`}
            label="Species"
            onChange={handleSelectChange}
          >
            {speciesOptions?.map((option, idx) => (
              <MenuItem key={option} value={idx}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          id="herd-animal-count-input"
          name="num_cows"
          label="Animal Count"
          value={formState.num_cows}
          type="number"
          onChange={handleInputChange}
          onKeyDown={handleNumCowsKeydown}
          disabled={userInfo.orgTypeAbbrev === 'RE'}
          sx={{width: 150}}
        />
      </Stack>

      <Stack direction={usePdfLayout ? 'column' : {xs: 'column', sm: 'row'}} spacing={2}>
        <FormControl
          disabled={userInfo.orgTypeAbbrev === 'FSP' || userInfo.orgTypeAbbrev === 'RE'}
          sx={{width: usePdfLayout ? '100%' : {xs: '100%', sm: '50%'}}}
        >
          <InputLabel id="herd-FSA-select-label">FSA</InputLabel>
          <Select
            labelId="herd-FSA-select-label"
            id="herd-FSA-select"
            name="fsp_code"
            value={`${formState.fsp_code === null ? '' : formState.fsp_code}`}
            label="FSA"
            onChange={handleSelectChange}
            autoWidth={true}
            MenuProps={{slotProps: {paper: {sx: {maxWidth: isPhone ? '60vw' : 'none'}}}}}
          >
            <MenuItem value="">No FSA Selected</MenuItem>
            {FSPOptions?.map((option) => (
              <MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          disabled={userInfo.orgTypeAbbrev === 'DRPC' || userInfo.orgTypeAbbrev === 'RE'}
          sx={{width: usePdfLayout ? '100%' : {xs: '100%', sm: '50%'}}}
        >
          <InputLabel id="herd-DRPC-select-label" shrink>DRPC</InputLabel>
          <Select
            labelId="herd-DRPC-select-label"
            id="herd-DRPC-select"
            name="drpc_code"
            value={`${formState.drpc_code === null ? '' : formState.drpc_code}`}
            label="DRPC"
            onChange={handleSelectChange}
            autoWidth={true}
            displayEmpty
            MenuProps={{slotProps: {paper: {sx: {maxWidth: isPhone ? '60vw' : 'none'}}}}}
          >
            <MenuItem value="">Non-DRPC Herd</MenuItem>
            {DRPCOptions?.map((option) => (
              <MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={usePdfLayout ? 'column' : {xs: 'column', sm: 'row'}} spacing={2}>
        <FormControl
          sx={{width: usePdfLayout ? '100%' : {xs: '100%', sm: '50%'}}}
        >
          <TextField
              id="person-providing-info-input"
              name="person_providing_info"
              label="Person Providing Info"
              value={formState.person_providing_info || ''}
              onChange={handleInputChange}
              disabled={userInfo.orgTypeAbbrev === 'RE'}
          />
        </FormControl>

        <DatePickerWrapper
          label='Person Providing Info Date'
          selectedDate={formState.person_providing_info_date}
          onChange={handleDateChange}
          disabled={!formState.person_providing_info || userInfo.orgTypeAbbrev === 'RE'}
          isDesktop={!isPhone || usePdfLayout}
          // @ts-ignore
          usePdfLayout={usePdfLayout}
          sx={{width: usePdfLayout ? '100%' : {xs: '100%', sm: '50%'}}}
        />
      </Stack>
    </Stack>
  )
}